import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDocs, orderBy, query, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Link } from 'react-router-dom';
import type { Listing } from '@shared/types';
import toast from 'react-hot-toast';
import { rebuildBrowseListingsCache } from '../../lib/browseListingsCache';
import { PUBLISHED_LISTINGS_QUERY_KEY } from '../../hooks/usePublishedListings';

export default function Listings() {
  const queryClient = useQueryClient();
  const { data: listings, isLoading } = useQuery({
    queryKey: ['admin-listings'],
    queryFn: async () => {
      const q = query(collection(db, 'listings'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Listing[];
    },
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'archived'>(
    'all'
  );
  const [featuredFilter, setFeaturedFilter] = useState<'all' | 'featured' | 'not_featured'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const filtered = useMemo(() => {
    const src = listings ?? [];
    const q = search.trim().toLowerCase();
    return src.filter((l) => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (featuredFilter === 'featured' && !l.featured) return false;
      if (featuredFilter === 'not_featured' && l.featured) return false;
      if (!q) return true;
      const hay = `${l.title ?? ''} ${l.location?.city ?? ''} ${l.location?.region ?? ''} ${
        l.location?.country ?? ''
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [listings, search, statusFilter, featuredFilter]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id));

  const toggleSelectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((l) => next.delete(l.id));
      } else {
        filtered.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  const runBulk = async (label: string, fn: (batch: ReturnType<typeof writeBatch>) => void) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    const t = toast.loading(label);
    try {
      const batch = writeBatch(db);
      fn(batch);
      await batch.commit();
      try {
        await rebuildBrowseListingsCache();
      } catch (cacheErr) {
        console.warn('Browse cache rebuild failed', cacheErr);
      }
      await queryClient.invalidateQueries({ queryKey: ['admin-listings'] });
      await queryClient.invalidateQueries({ queryKey: PUBLISHED_LISTINGS_QUERY_KEY });
      toast.success('Done.', { id: t });
      setSelectedIds(new Set());
    } catch (e: any) {
      toast.error(e?.message || 'Bulk action failed', { id: t });
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetStatus = async (status: 'draft' | 'published' | 'archived') => {
    await runBulk(`Setting status → ${status}…`, (batch) => {
      const now = Timestamp.now();
      selectedIds.forEach((id) => {
        batch.set(
          doc(db, 'listings', id),
          { status, updatedAt: now },
          { merge: true }
        );
      });
    });
  };

  const bulkSetFeatured = async (featured: boolean) => {
    await runBulk(`${featured ? 'Featuring' : 'Unfeaturing'} listings…`, (batch) => {
      const now = Timestamp.now();
      selectedIds.forEach((id) => {
        batch.set(
          doc(db, 'listings', id),
          { featured, updatedAt: now },
          { merge: true }
        );
      });
    });
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = window.confirm(
      `Delete ${selectedIds.size} listing(s)? This cannot be undone.`
    );
    if (!ok) return;
    await runBulk('Deleting listings…', (batch) => {
      selectedIds.forEach((id) => batch.delete(doc(db, 'listings', id)));
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#32373c]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold">Listings</h1>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/import/wp-rentals" className="btn btn-secondary">
            Import from WP Rentals
          </Link>
          <Link to="/admin/listings/new" className="btn btn-primary">
            Add New Listing
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
          <input
            className="input w-full"
            placeholder="Title, city, region…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
          <select
            className="input w-full"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="min-w-[180px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Featured</label>
          <select
            className="input w-full"
            value={featuredFilter}
            onChange={(e) => setFeaturedFilter(e.target.value as any)}
          >
            <option value="all">All</option>
            <option value="featured">Featured only</option>
            <option value="not_featured">Not featured</option>
          </select>
        </div>

        <div className="ml-auto flex gap-2 flex-wrap items-center">
          <span className="text-xs text-gray-500">
            Showing <strong>{filtered.length}</strong>
            {listings ? ` / ${listings.length}` : ''}
          </span>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="bg-white rounded-lg shadow p-3 mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm">
            <strong>{selectedCount}</strong> selected
          </span>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => bulkSetStatus('draft')}
            disabled={bulkBusy}
          >
            Set draft
          </button>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => bulkSetStatus('published')}
            disabled={bulkBusy}
          >
            Set published
          </button>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => bulkSetStatus('archived')}
            disabled={bulkBusy}
          >
            Set archived
          </button>
          <span className="text-gray-300 mx-1">|</span>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => bulkSetFeatured(true)}
            disabled={bulkBusy}
          >
            Mark featured
          </button>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => bulkSetFeatured(false)}
            disabled={bulkBusy}
          >
            Unfeature
          </button>
          <span className="text-gray-300 mx-1">|</span>
          <button
            type="button"
            className="btn btn-secondary text-sm text-red-700"
            onClick={bulkDelete}
            disabled={bulkBusy}
          >
            Delete
          </button>
          <button
            type="button"
            className="btn btn-secondary text-sm ml-auto"
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkBusy}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3">
        <div className="bg-white rounded-lg shadow p-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAllFiltered} />
            Select all (filtered)
          </label>
          <span className="text-xs text-gray-500">{filtered.length} shown</span>
        </div>
        {filtered.map((listing) => (
          <div key={listing.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={selectedIds.has(listing.id)}
                onChange={() => toggleSelected(listing.id)}
                aria-label={`Select ${listing.title}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{listing.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {listing.location?.city || '-'}, {listing.location?.country || '-'}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                    R {Math.round((listing.basePrice || 0) / 100).toLocaleString('en-ZA')}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      listing.status === 'published'
                        ? 'bg-green-100 text-green-800'
                        : listing.status === 'draft'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {listing.status}
                  </span>
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      listing.featured ? 'bg-emerald-100 text-emerald-900' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {listing.featured ? 'Featured' : 'Not featured'}
                  </span>
                  <div className="ml-auto flex gap-3 text-sm">
                    <Link
                      to={`/admin/listings/${listing.id}`}
                      className="text-[#32373c] hover:text-[#2a2e33] font-medium"
                    >
                      Edit
                    </Link>
                    <Link
                      to={`/admin/listings/${listing.id}/calendar`}
                      className="text-[#32373c] hover:text-[#2a2e33] font-medium"
                    >
                      Calendar
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white rounded-lg shadow p-8 text-sm text-gray-500">
            No listings match your filters.
          </div>
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAllFiltered}
                  aria-label="Select all filtered"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Featured
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
            {filtered?.map((listing) => (
              <tr key={listing.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(listing.id)}
                    onChange={() => toggleSelected(listing.id)}
                    aria-label={`Select ${listing.title}`}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{listing.title}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {listing.location?.city || '-'}, {listing.location?.country || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    R {Math.round((listing.basePrice || 0) / 100).toLocaleString('en-ZA')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {listing.featured ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-900">
                      Yes
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700">
                      No
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      listing.status === 'published'
                        ? 'bg-green-100 text-green-800'
                        : listing.status === 'draft'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {listing.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <Link
                    to={`/admin/listings/${listing.id}`}
                    className="text-[#32373c] hover:text-[#2a2e33]"
                  >
                    Edit
                  </Link>
                  <span className="text-gray-300">|</span>
                  <Link
                    to={`/admin/listings/${listing.id}/calendar`}
                    className="text-[#32373c] hover:text-[#2a2e33]"
                  >
                    Calendar
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td className="px-6 py-10 text-sm text-gray-500" colSpan={7}>
                  No listings match your filters.
                </td>
              </tr>
            )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

